import Link from "next/link";
import Script from "next/script";

import { renderToString } from "react-dom/server";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PRICING_PLANS } from "@/lib/constants/pricing";

const maxBuiltRightPricePoint = Object.values(PRICING_PLANS).reduce((max, plan) => Math.max(max, plan.price), 0);

const getPricingRange = (industry: string, population = 100000, businessType = "small") => {
  const citySizes = [
    { name: "mega", min: 5000000, max: Infinity, multiplier: 3.0 },
    { name: "veryLarge", min: 2500000, max: 4999999, multiplier: 2.5 },
    { name: "large", min: 1000000, max: 2499999, multiplier: 2.0 },
    { name: "mediumLarge", min: 500000, max: 999999, multiplier: 1.5 },
    { name: "medium", min: 250000, max: 499999, multiplier: 1.4 },
    { name: "mediumSmall", min: 100000, max: 249999, multiplier: 1.3 },
    { name: "small", min: 50000, max: 99999, multiplier: 1.2 },
    { name: "verySmall", min: 25000, max: 49999, multiplier: 1.1 },
    { name: "tiny", min: 0, max: 24999, multiplier: 1.0 },
  ];

  const sizeMultiplier = citySizes.find((size) => population >= size.min && population <= size.max)?.multiplier || 1.5;

  // business type multiplier
  const businessMultiplier = businessType.toLowerCase() === "enterprise" ? 2.5 : 1;

  // combined multiplier
  const totalMultiplier = sizeMultiplier * businessMultiplier;

  // industry specific base ranges
  const baseRanges: Record<string, { min: number; max: number }> = {
    "Carpet Cleaning": { min: 3500, max: 8500 },
    HVAC: { min: 8500, max: 20000 },
    Plumbing: { min: 8500, max: 20000 },
    "Home Remodeling": { min: 8500, max: 22000 },
    Landscaping: { min: 6500, max: 15000 },
    Painting: { min: 4000, max: 10000 },
    "Personal Trainer": { min: 2800, max: 6500 },
    Moving: { min: 3500, max: 8500 },
    Roofing: { min: 8500, max: 22000 },
    "Pest Control": { min: 3500, max: 9000 },
    "Pool Cleaning": { min: 3200, max: 8500 },
    "Pressure Washing": { min: 4500, max: 11000 },
    "Tree Services": { min: 6500, max: 15000 },
    Electrician: { min: 8500, max: 20000 },
    "Car Wash": { min: 4500, max: 12000 },
    "Cleaning / Maid Service": { min: 3500, max: 9000 },
    "Window Cleaning": { min: 3800, max: 9500 },
  };

  const range = baseRanges[industry] || { min: 4000, max: 12000 };

  return {
    min: Math.round((range.min * totalMultiplier) / 5) * 5, // round to nearest 5
    max: Math.round((range.max * totalMultiplier) / 5) * 5, // round to nearest 5
  };
};

const FAQs = ({
  industry,
  city,
  stateAbbr,
  population,
}: {
  industry: string;
  city?: string;
  stateAbbr?: string;
  population?: number;
}) => {
  const isLocationSpecific = Boolean(city && stateAbbr);

  const { min: smallMin, max: smallMax } = getPricingRange(industry, population || 500000, "small");
  const { min: enterpriseMin, max: enterpriseMax } = getPricingRange(industry, population || 500000, "enterprise");

  const locationPrefix = isLocationSpecific ? `in ${city}, ${stateAbbr}` : "";
  const locationSuffix = isLocationSpecific ? `for ${city} businesses` : "for businesses";

  const faqs = [
    {
      question: `How much does a ${industry} website cost${isLocationSpecific ? ` ${locationPrefix}` : ""}?`,
      answer: (
        <>
          Professional {industry} websites typically range from ${smallMin}-${smallMax} for small businesses to $
          {enterpriseMin}-${enterpriseMax} for enterprise solutions, and that&apos;s before the monthly retainer or
          maintenance fee for hosting and content updates. But at Rebolt, our pricing caps out at $
          {maxBuiltRightPricePoint}/month - you can learn more about our pricing&nbsp;
          <Link href={isLocationSpecific ? "#pricing" : "/pricing"} className="underline hover:text-rebolt-600">
            here
          </Link>
          . Our {industry} websites deliver an average ROI of 3.2x within the first year {locationSuffix}.
        </>
      ),
    },
    {
      question: `What features should a ${industry} website include to succeed${isLocationSpecific ? ` ${locationPrefix}` : ""}?`,
      answer: `A successful ${industry} website should include mobile-responsive design, fast loading speeds, clear calls-to-action, and SEO optimization. Essential features include online booking/scheduling, customer testimonials, service galleries, and secure contact forms. ${isLocationSpecific ? `We also recommend incorporating local ${city} content and` : "We also recommend"} ensuring ADA compliance for maximum market reach.`,
    },
    {
      question: `How long does it take to build a ${industry} website for a business?`,
      answer: `Typically, a custom ${industry} website takes 4-8 weeks from start to finish. With BuiltRight's streamlined process, we can launch your professional ${industry} website in 30 minutes or less. This includes design, development, content creation, and thorough testing to ensure everything works perfectly ${isLocationSpecific ? `for your ${city} customers` : "for your customers"}.`,
    },
    {
      question: `What ROI can ${industry} businesses expect${isLocationSpecific ? ` ${locationPrefix}` : ""}?`,
      answer: `${isLocationSpecific ? `${city} ` : ""}${industry} businesses typically see a 250-400% return on their website investment within the first year. Our clients average 35% more leads after launching their new website, with conversion rates improving by 25-40%. We've helped ${isLocationSpecific ? "local" : ""} businesses increase their online visibility by up to 80% and reduce customer acquisition costs by 30% through effective web design and optimization.`,
    },
    {
      question: `How will my ${industry} website compete with others${isLocationSpecific ? ` ${locationPrefix}` : ""}?`,
      answer: `Your website will stand out ${isLocationSpecific ? `in the ${city} market` : "in the market"} through our proven combination of ${isLocationSpecific ? "local " : ""}SEO optimization, conversion-focused design, and industry-specific features. We analyze your top ${isLocationSpecific ? "local " : ""}competitors and implement strategies to outperform them in search rankings, user experience, and mobile responsiveness. Plus, our ongoing optimization ensures your site stays ahead of market trends and algorithm updates.`,
    },
  ];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text:
          typeof faq.answer === "string"
            ? faq.answer
            : renderToString(faq.answer).replace(/<(?!\/?a(?:\s[^>]*)?)[^>]+>/g, ""), // strip all tags except <a>
      },
    })),
  };

  return (
    <section className="container mx-auto py-24">
      <Script
        id="faq-structured-data"
        type="application/ld+json"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <h2 className="text-3xl lg:text-4xl text-ash-800 font-semibold text-center mb-10 lg:mb-16 max-w-2xl mx-auto w-full leading-[1.35]">
        Frequently asked questions
      </h2>

      <Accordion type="single" collapsible className="max-w-2xl mx-auto px-4 lg:px-0">
        {faqs.map((faq, index) => (
          <AccordionItem key={index} className="mt-2" value={`item-${index}`}>
            <AccordionTrigger className="text-lg text-left">{faq.question}</AccordionTrigger>
            <AccordionContent forceMount className="text-ash-600 leading-loose! prose-lg text-base mb-4">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};

export default FAQs;
